/* eslint-disable */
import type { NSFWDetectionConfig, NSFWDetectionResult, NSFWPrediction } from '../core/types'

/** Fully-resolved internal config used after normalizing user input. */
interface NormalizedNSFWConfig {
  sampleIntervalMs: number
  threshold: number
  model: 'MobileNetV2' | 'MobileNetV2Mid' | 'InceptionV3'
  categories: string[]
  source: 'local' | 'remote'
  autoBlur: boolean
  windowSize: number
  minExplicitInWindow: number
  cooldownMs: number
}

/** Callbacks that {@link NSFWDetector} invokes during its lifecycle. */
export interface NSFWDetectorCallbacks {
  /** Called when explicit content is detected above the threshold. */
  onDetection: (result: NSFWDetectionResult) => void
  /** Called when an error occurs during model load or classification. */
  onError: (error: any) => void
  /** Called when the model starts loading or becomes ready. */
  onStatus: (status: 'loading' | 'ready') => void
}

/**
 * Manages the `nsfwjs` model lifecycle and frame classification.
 *
 * Uses lazy loading: the model (~30 MB for InceptionV3 default) is
 * only downloaded when the consumer calls {@link loadModel}.
 * Classification runs via tf.js WebGL backend (GPU-accelerated, async)
 * so it does not block the main thread significantly. Combined with a
 * 1500 ms+ sampling interval this keeps CPU overhead < 5 % on
 * mid-range hardware.
 */
export class NSFWDetector {
  private model: any = null
  private config: NormalizedNSFWConfig
  private callbacks: NSFWDetectorCallbacks
  private frameIndex = 0
  private loading = false
  private disposed = false
  private analysisCount = 0
  private totalLatencyMs = 0
  private recentResults: boolean[] = []
  private lastDetectionTime = 0

  /**
   * @param config    - Detection configuration (threshold, model, etc.).
   * @param callbacks - Lifecycle callbacks for detection results, errors
   *                    and status changes.
   */
  constructor (config: NSFWDetectionConfig, callbacks: NSFWDetectorCallbacks) {
    this.config = NSFWDetector.normalizeConfig(config)
    this.callbacks = callbacks
  }

  /**
   * Download and initialize the NSFW classification model.
   *
   * This is intentionally lazy — the model is only fetched when the
   * consumer explicitly enables NSFW detection. The method
   * is idempotent: calling it while loading or after the model is
   * already loaded is a no-op.
   *
   * @throws If the model download or initialization fails.
   */
  async loadModel (): Promise<void> {
    if (this.model || this.loading) return

    this.loading = true
    this.callbacks.onStatus('loading')

    try {
      const nsfwModule = await import('nsfwjs')
      const loadFn = nsfwModule.load || (nsfwModule as any).default?.load
      if (!loadFn) throw new Error('nsfwjs.load not found')

      const modelName = this.config.model
      const options: Record<string, any> = {}

      if (modelName === 'InceptionV3') {
        options.size = 299
      } else if (modelName === 'MobileNetV2Mid') {
        options.type = 'graph'
      }

      this.model = await loadFn(modelName, options)
      this.loading = false

      if (!this.disposed) {
        this.callbacks.onStatus('ready')
      }
    } catch (err) {
      this.loading = false
      this.callbacks.onError(err)
      throw err
    }
  }

  /**
   * Classify an image source and return the detection result.
   *
   * Uses a sliding window of the last {@link NormalizedNSFWConfig.windowSize}
   * frames: fires `onDetection` when at least
   * {@link NormalizedNSFWConfig.minExplicitInWindow} of those frames are
   * explicit AND the {@link NormalizedNSFWConfig.cooldownMs} cooldown has
   * elapsed since the last emission. This tolerates the natural probability
   * fluctuation of webcam-captured content.
   *
   * @param source - An `HTMLCanvasElement` or `OffscreenCanvas` with
   *                 the captured video frame drawn on it.
   * @returns The full classification result, or `null` if the model
   *          is not loaded or the detector has been disposed.
   */
  async classify (source: HTMLCanvasElement | OffscreenCanvas): Promise<NSFWDetectionResult | null> {
    if (!this.model || this.disposed) return null

    const startTime = performance.now()

    try {
      const raw: Array<{ className: string; probability: number }> =
        await this.model.classify(source as any, 5)

      const latency = performance.now() - startTime
      this.analysisCount++
      this.totalLatencyMs += latency

      const predictions: NSFWPrediction[] = raw.map(p => ({
        className: p.className,
        probability: p.probability
      }))

      const topPrediction = predictions.reduce((a, b) =>
        a.probability > b.probability ? a : b
      )

      const isExplicit = this.config.categories.includes(topPrediction.className) &&
                         topPrediction.probability >= this.config.threshold

      this.recentResults.push(isExplicit)
      if (this.recentResults.length > this.config.windowSize) {
        this.recentResults.shift()
      }

      const explicitCount = this.recentResults.filter(Boolean).length

      this.frameIndex++

      const result: NSFWDetectionResult = {
        predictions,
        topPrediction,
        isExplicit,
        timestamp: Date.now(),
        source: this.config.source,
        frameIndex: this.frameIndex
      }

      const now = Date.now()
      const cooldownElapsed = (now - this.lastDetectionTime) >= this.config.cooldownMs
      const confirmed = explicitCount >= this.config.minExplicitInWindow

      if (confirmed && cooldownElapsed) {
        this.lastDetectionTime = now
        this.recentResults = []
        this.callbacks.onDetection(result)
      }

      return result
    } catch (err) {
      this.callbacks.onError(err)
      return null
    }
  }

  /** Whether the model has been successfully loaded. */
  isModelLoaded (): boolean {
    return this.model !== null
  }

  /** Whether the model is currently downloading / initializing. */
  isLoading (): boolean {
    return this.loading
  }

  /** Total number of frames that have been sampled (not necessarily explicit). */
  getFrameCount (): number {
    return this.frameIndex
  }

  /** Total number of successful classifications performed. */
  getAnalysisCount (): number {
    return this.analysisCount
  }

  /**
   * Average classification latency in milliseconds across all analyses.
   * Returns 0 if no analyses have been performed.
   */
  getAvgLatencyMs (): number {
    return this.analysisCount > 0 ? this.totalLatencyMs / this.analysisCount : 0
  }

  /**
   * Reset internal detection state so the next classification cycle
   * can trigger immediately, bypassing any remaining cooldown.
   * Useful after an explicit "continue" action by the operator.
   */
  resetDetectionState (): void {
    this.recentResults = []
    this.lastDetectionTime = 0
  }

  /**
   * Hot-update detection settings (threshold, categories, etc.)
   * without reloading the model.
   *
   * @param config - New (partial) detection configuration.
   */
  updateConfig (config: NSFWDetectionConfig): void {
    this.config = NSFWDetector.normalizeConfig(config)
    this.recentResults = []
  }

  /**
   * Dispose the loaded model and mark this detector as inactive.
   * After calling this, {@link classify} becomes a no-op.
   */
  async dispose (): Promise<void> {
    this.disposed = true
    this.recentResults = []
    if (this.model) {
      try {
        if (typeof this.model.dispose === 'function') {
          this.model.dispose()
        }
      } catch (_) {
        // Ignore disposal errors
      }
      this.model = null
    }
  }

  /**
   * Fill in missing config values with sensible defaults.
   *
   * @param config - Partial detection config.
   * @returns Fully-populated required config.
   */
  static normalizeConfig (config: NSFWDetectionConfig): NormalizedNSFWConfig {
    const c: any = config
    return {
      sampleIntervalMs: c.sampleIntervalMs ?? 1500,
      threshold: c.threshold ?? 0.60,
      model: c.model ?? 'InceptionV3',
      categories: c.categories ?? ['Porn', 'Hentai'],
      source: c.source ?? 'local',
      autoBlur: c.autoBlur ?? false,
      windowSize: c.windowSize ?? 5,
      minExplicitInWindow: c.minExplicitInWindow ?? 3,
      cooldownMs: c.cooldownMs ?? 30000
    }
  }
}
